import React, {useEffect, useState} from "react";
import axios from "axios";
import styles from "./EditarEndereco.module.css";
import {useTranslation} from "react-i18next";
import api from "../../utils/axios/api";
import {useParams} from "react-router-dom";

interface State {
  id: number;
  nome: string;
  regiao: {
    id: number;
    nome: string;
    sigla: string;
  };
  sigla: string;
}

interface City {
  id: number;
  nome: string;
}

interface Address {
  name?: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  state: string;
  city: string;
}

function EditarEndereco() {
  const { t } = useTranslation();
  const [estados, setEstados] = useState<State[]>([]);
  const [address, setAddress] = useState<Address>({
    name: "",
    zipCode: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    state: "",
    city: ""
  });
  const [cities, setCities] = useState<City[]>([]);
  const { id: addressId } = useParams();

  useEffect(() => {
    async function fetchInitialData() {
      const responseEstados = await axios.get("https://servicodados.ibge.gov.br/api/v1/localidades/estados");
      setEstados(responseEstados.data);
      const responseAddress = await api.get(`/address/${addressId}`);
      if (responseAddress.data) {
        setAddress(responseAddress.data);
        if (responseAddress.data.state) {
          fetchCities(responseAddress.data.state);
        }
      }
    }
    fetchInitialData();
  }, [addressId]);

  const fetchCities = async (stateSigla: string) => {
    const response = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateSigla}/municipios`);
    setCities(response.data);
  };

  const handleInputChange = (field: keyof Address, value: string) => {
    setAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const state = event.target.value;
    setAddress(prev => ({
      ...prev,
      state
    }));
    fetchCities(state);
  };

  const handleCepBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const cep = event.target.value.replace(/\D/g, "");
    if (cep.length === 8) {
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
        const data = response.data;
        if (!data.erro) {
          setAddress(prev => ({
            ...prev,
            street: data.logradouro,
            district: data.bairro,
            city: data.localidade,
            state: data.uf,
            zipCode: cep
          }));
          fetchCities(data.uf);
        } else {
          console.error("CEP não encontrado.");
        }
      } catch (error) {
        console.error("Erro ao buscar dados do CEP:", error);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const response = await api.put(`/address/${addressId}`, address);
      console.log("Dados atualizados com sucesso:", response.data);
      window.alert(`${t(`accountMenu.userAddressUpdated`)}!`);
      window.location.reload();
    } catch (error) {
      console.error("Erro ao atualizar dados:", error);
    }
  };

  return (
    <div className={styles.main_container}>
      <h1>{t(`accountMenu.editAddress`)}</h1>
      <form className={styles.campo} onSubmit={handleSubmit}>
        <div className={styles.roll}>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressName">{t(`accountMenu.nickname`)}</label>
            <input
              id="editAddressName"
              type="text"
              placeholder="Casa"
              value={address.name ?? ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressZipCode" style={{ textTransform: "uppercase" }}>{t(`accountMenu.cep`)}</label>
            <input
              id="editAddressZipCode"
              type="text"
              onBlur={handleCepBlur}
              placeholder="12345678"
              value={address.zipCode}
              onChange={(e) => handleInputChange("zipCode", e.target.value)}
            />
          </div>
        </div>
        <div className={styles.roll}>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressStreet">{t(`accountMenu.address`)}</label>
            <input
              id="editAddressStreet"
              type="text"
              placeholder="Rua James Cameron"
              value={address.street}
              onChange={(e) => handleInputChange("street", e.target.value)}
            />
          </div>
          <div className = {styles.NumAndComp}>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressNumber">{t(`accountMenu.houseNumber`)}</label>
            <input
              id="editAddressNumber"
              type="text"
              placeholder="Ex: 123 ou S/N"
              value={address.number}
              onChange={(e) => handleInputChange("number", e.target.value)}
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressComplement">{t(`cadastro.complement`)}</label>
            <input
              id="editAddressComplement"
              type="text"
              placeholder="Kitnet 123"
              value={address.complement}
              onChange={(e) => handleInputChange("complement", e.target.value)}
            />
          </div>
          </div>
        </div>
        <div className={styles.roll}>
          <div className={styles.inputContainer}>
            <label htmlFor="editAddressDistrict">{t(`accountMenu.district`)}</label>
            <input
              id="editAddressDistrict"
              type="text"
              placeholder="Jardim América"
              value={address.district}
              onChange={(e) => handleInputChange("district", e.target.value)}
            />
          </div>
          <div className = {styles.NumAndComp}>
          <div className={styles.inputContainer}>
            <label htmlFor="selectState">{t(`accountMenu.state`)}</label>
            <select
              id="selectState"
              className={styles.select}
              value={address.state}
              onChange={handleStateChange}
            >
              <option value="" disabled>{t(`accountMenu.select`)}</option>
              {estados.map((estado) => (
                <option key={estado.id} value={estado.sigla}>{estado.sigla}</option>
              ))}
            </select>
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="selectCity">{t(`accountMenu.city`)}</label>
            <select
              id="selectCity"
              className={styles.select}
              value={address.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              disabled={!address.state}
            >
              <option value="" disabled>{t(`accountMenu.select`)}</option>
              {cities.map((city) => (
                <option key={city.id} value={city.nome}>{city.nome}</option>
              ))}
            </select>
          </div>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button type="submit">{t(`accountMenu.save`)}</button>
        </div>
      </form>
    </div>
  );
}

export default EditarEndereco;
